import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { safeRedirectTarget } from '@/lib/safe-redirect';

import { useSession } from './useSession';

type Mode = 'requireSignedOut' | 'requireSignedIn';

type Options = {
  mode: Mode;
  /** Where to send the user when the gate fails. */
  fallback: string;
  /** Optional override for the post-redirect URL when reading ?redirect= from query. */
  defaultTarget?: string;
  /**
   * When false, the redirect effect is suppressed even if the gate would
   * otherwise fire. Useful while a form is mid-submit and the page is
   * about to make its own navigation decision based on a freshly-fetched
   * profile (auth cross-entrypoint role gate, 2026-05-05). Defaults to true.
   */
  enabled?: boolean;
};

/**
 * Auth-aware redirect helper:
 *  - `requireSignedOut`: if a session exists (e.g. user lands on /signin while
 *    already authenticated), bounce them to ?redirect= or `defaultTarget`.
 *  - `requireSignedIn`: if no session, send them to `fallback` (typically /signin).
 */
export function useAuthRedirect({
  mode,
  fallback,
  defaultTarget = '/dashboard',
  enabled = true,
}: Options): {
  loading: boolean;
} {
  const { loading, session } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;
    if (mode === 'requireSignedOut' && session) {
      const target = safeRedirectTarget(params.get('redirect'), defaultTarget);
      navigate(target, { replace: true });
    }
    if (mode === 'requireSignedIn' && !session) {
      navigate(fallback, { replace: true });
    }
  }, [enabled, loading, session, mode, fallback, defaultTarget, navigate, params]);

  return { loading };
}
